import SignalsmithStretch, { type StretchNode } from 'signalsmith-stretch';
import { processingFor } from '../audio/pitch-policy';
import { STEMS, type BufferPort, type BufferedTransportPort, type PlaybackSettings, type Section, type StemId } from '../audio/types';

async function request<T>(promise: Promise<T>): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try { return await Promise.race([promise, new Promise<T>((_,reject) => { timer=setTimeout(()=>reject(new Error('The audio processor did not respond. Reload the page.')),20000); })]); }
  finally { if(timer)clearTimeout(timer); }
}

export class StretchTransport implements BufferedTransportPort {
  leadSeconds = .25;
  private closed = false;
  private failed?: string;
  private splitters: ChannelSplitterNode[] = [];
  private mergers: ChannelMergerNode[] = [];
  onFailure?: (message: string) => void;
  private constructor(private context: AudioContext, private groups: { node: StretchNode; stems: StemId[] }[]) {
    for (const {node, stems} of groups) {
      const splitter = context.createChannelSplitter(stems.length * 2);
      node.connect(splitter); this.splitters.push(splitter);
      node.addEventListener('processorerror', () => { this.failed = 'The audio processor stopped. Reload the page.'; this.onFailure?.(this.failed); });
    }
  }
  static async create(context: AudioContext): Promise<StretchTransport> {
    const groups: {node: StretchNode; stems: StemId[]}[] = [];
    try {
      // Keep Band and Rhythm phase-linked; analyse the voice and click separately.
      for (const stems of [['click'], ['vocals'], ['band','rhythm']] as StemId[][]) {
        const channels = stems.length * 2;
        const node = await request(SignalsmithStretch(context, { numberOfInputs: 1, numberOfOutputs: 1, outputChannelCount: [channels], channelCount: channels, channelCountMode: 'explicit', channelInterpretation: 'discrete' }));
        groups.push({node,stems});
        await request(node.configure({ preset: 'default', blockMs: 0, splitComputation: true }));
      }
      const transport = new StretchTransport(context, groups);
      transport.leadSeconds = Math.max(.25, ...await Promise.all(groups.map(async ({node}) => await request(node.latency()) + .1)));
      return transport;
    } catch (error) { for(const {node} of groups){node.disconnect();node.port.close();} throw error; }
  }
  connectChannels(faders: Record<StemId, GainNode>): void {
    this.groups.forEach(({stems},group) => stems.forEach((id,index) => {
      const stereo = this.context.createChannelMerger(2), splitter=this.splitters[group];
      splitter.connect(stereo,index*2,0); splitter.connect(stereo,index*2+1,1);
      stereo.connect(faders[id]);this.mergers.push(stereo);
    }));
  }
  async load(buffers: Partial<Record<StemId, BufferPort>>): Promise<void> {
    this.assertLive();
    await Promise.all(this.groups.map(async ({node}) => {
      await request(node.schedule({ outputTime: this.context.currentTime, output: this.context.currentTime, active: false }));
      await request(node.dropBuffers());
    }));
    if (Object.keys(buffers).length !== 4) return;
    // Copy one stereo stem at a time and release its AudioBuffer immediately.
    // The final eight arrays are transferred, not cloned, into the worklet.
    for (const {node,stems} of this.groups) {
      const samples: Float32Array[] = [];
      for (const id of stems) {
      let buffer: BufferPort | undefined = buffers[id]!;
      samples.push(buffer.getChannelData(0).slice(), buffer.getChannelData(1).slice());
      const { length, duration, sampleRate, numberOfChannels } = buffer;
      buffers[id] = { length, duration, sampleRate, numberOfChannels, getChannelData() { throw new Error('Samples are owned by the audio worklet.'); } };
      buffer = undefined;
      await new Promise(resolve => setTimeout(resolve, 0));
      this.assertLive();
      }
      await request(node.addBuffers(samples, samples.map(sample => sample.buffer as ArrayBuffer)));
    }
    this.assertLive();
  }
  async start(when: number, offset: number, settings: PlaybackSettings, loop: Section | null): Promise<void> {
    this.assertLive();
    if(loop)throw new Error('Section looping is unavailable in this timeline-only web mixer.');
    try {
      await Promise.all(this.groups.map(({node,stems}) => request(node.schedule({ outputTime: when, output: when, active: true, input: offset,
        ...processingFor(stems[0],settings),
        loopStart: 0, loopEnd: 0 }))));
      if (this.context.currentTime >= when) throw new Error('Audio scheduling took too long. Press Play again.');
    } catch(error) { this.stop(this.context.currentTime); throw error; }
  }
  stop(when: number): void {
    if (!this.closed && !this.failed) for(const {node} of this.groups) void request(node.schedule({ outputTime: this.context.currentTime, output: when, active: false })).catch(error=>{this.failed=String(error);this.onFailure?.(this.failed);});
  }
  dispose(): void {
    this.closed = true; this.groups.forEach(({node})=>{node.disconnect();node.port.close();});
    this.splitters.forEach(node=>node.disconnect());this.mergers.forEach(node => node.disconnect());
  }
  private assertLive(): void {
    if (this.closed) throw new Error('Audio processor is closed.');
    if (this.failed) throw new Error(this.failed);
  }
}
